import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Card } from "../../components/ui/Card";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { AppTheme } from "../../AppTheme";

// Import your service
import {
  createTemplate,
  getAllTemplates,
} from "../../service/template/TemplateServices";
import { CreateTemplateDto } from "@/types/temp.types";

const templateTypeOptions = [
  { label: "Standard", value: "standard" },
  { label: "Professional", value: "professional" },
  { label: "Modern", value: "modern" },
  { label: "Simple", value: "simple" },
  { label: "Detailed", value: "detailed" },
];

// This is a placeholder component for creating a new template
export const CreateTemplate: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useRef<Toast>(null);

  const [template, setTemplate] = useState<CreateTemplateDto>({
    template_name: "",
    template_type: "",
    template_content: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleInputChange = (value: string, field: keyof CreateTemplateDto) => {
    setTemplate((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field if it exists
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!template.template_name.trim()) {
      newErrors.template_name = "Template name is required";
    }

    if (!template.template_type) {
      newErrors.template_type = "Template type is required";
    }

    if (!template.template_content.trim()) {
      newErrors.template_content = "Template content is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateTemplate = async () => {
    if (!validateForm()) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please fill in all required fields",
        life: 3000,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // In a real app, you would call the createTemplate API
      const response = await createTemplate(dispatch, template);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Template created successfully",
          life: 3000,
        });
      }

      // Navigate back to template list after successful creation
      setTimeout(() => {
        navigate("/template");
      }, 1500);
    } catch (error) {
      console.error("Error creating template:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to create template. Please try again.",
        life: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Toast ref={toast} />

      <div className="flex justify-between items-center mb-6">
        <h1
          className={`text-${AppTheme.font.size["2xl"]} font-bold text-gray-800`}
        >
          Create New Template
        </h1>

        <Button
          label="Back to Templates"
          icon="pi pi-arrow-left"
          className="p-button-outlined"
          onClick={() => navigate("/templates")}
        />
      </div>

      <Card>
        <div className="p-fluid p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="field">
              <label
                htmlFor="template_name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Template Name *
              </label>
              <InputText
                id="template_name"
                value={template.template_name}
                onChange={(e) =>
                  handleInputChange(e.target.value, "template_name")
                }
                className={errors.template_name ? "p-invalid" : ""}
              />
              {errors.template_name && (
                <small className="p-error">{errors.template_name}</small>
              )}
            </div>

            <div className="field">
              <label
                htmlFor="template_type"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Template Type *
              </label>
              <Dropdown
                id="template_type"
                value={template.template_type}
                options={templateTypeOptions}
                onChange={(e) => handleInputChange(e.value, "template_type")}
                placeholder="Select a template type"
                className={errors.template_type ? "p-invalid" : ""}
              />
              {errors.template_type && (
                <small className="p-error">{errors.template_type}</small>
              )}
            </div>
          </div>

          <div className="field mb-4">
            <label
              htmlFor="template_content"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Template Content *
            </label>
            <InputTextarea
              id="template_content"
              value={template.template_content}
              onChange={(e) =>
                handleInputChange(e.target.value, "template_content")
              }
              rows={10}
              className={errors.template_content ? "p-invalid" : ""}
            />
            {errors.template_content && (
              <small className="p-error">{errors.template_content}</small>
            )}
            <small className="text-gray-600 block mt-1">
              Enter HTML/CSS template content. You can use placeholders like{" "}
              {"{BUSINESS_NAME}"}, {"{INVOICE_NUMBER}"}, etc.
            </small>
          </div>

          <div className="flex justify-end">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text mr-2"
              onClick={() => navigate("/templates")}
            />
            {/* <Button
              label="Create Template"
              icon="pi pi-check"
              onClick={handleCreateTemplate}
              loading={isSubmitting}
            /> */}
          </div>
        </div>
      </Card>
    </div>
  );
};
